import { readonly, ref, type Ref } from "vue";

/*****************************************************************************
 * Open state and keyboard shortcuts for the command palette (nav-10).
 *
 * Module-scope state, like useMobileSidebarMenu: the palette itself is mounted
 * once by PrivateHeader, and its triggers (the sidebar button, Cmd/Ctrl+K and
 * '/') live elsewhere.
 *****************************************************************************/

const isOpen = ref< boolean >( false );

// The mounted palette's show function, called in the same task as the tap or key that opens it (see CommandPalette).
let opener: ( () => void ) | null = null;

/** For CommandPalette only. Returns the unregister function. */
export function registerPaletteOpener( show: () => void ): () => void {
    opener = show;
    return () => {
        if ( opener === show ) opener = null;
    };
}

export function useCommandPalette(): {
    isPaletteOpen: Readonly< Ref< boolean > >,
    openPalette: () => void,
    closePalette: () => void,
} {
    return {
        isPaletteOpen: readonly( isOpen ),
        openPalette: () => {
            isOpen.value = true;
            opener?.();
        },
        closePalette: () => { isOpen.value = false; },
    };
}

/** macOS and iOS use Cmd, everything else Ctrl; also what the shortcut hint shows. */
export function isApplePlatform(): boolean {
    if ( typeof navigator === "undefined" ) return false;
    return /Mac|iPhone|iPad|iPod/.test( navigator.platform || navigator.userAgent );
}

/** An element that takes typed text, where '/' is a character and not a shortcut. */
export function isEditableTarget( target: EventTarget | null ): boolean {
    if ( !( target instanceof HTMLElement ) ) return false;
    if ( target.isContentEditable ) return true;
    if ( target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement ) return true;
    if ( target instanceof HTMLInputElement ) {
        // Buttons, checkboxes and the like take no text.
        return ![ "button", "checkbox", "color", "file", "image", "radio", "range", "reset", "submit" ].includes( target.type );
    }
    return false;
}

export type PaletteShortcut = "toggle" | "open" | null;

/**
 * What a keydown means to the palette.
 *
 * - Cmd+K (Apple) or Ctrl+K (elsewhere) toggles it from anywhere, text fields included; the other modifier is left
 *   alone because Ctrl+K deletes to the end of the line in a macOS text field.
 * - '/' opens it, but only outside text fields and outside another dialog, where it is a character or belongs to
 *   that dialog. Shift is allowed, since '/' needs it on some layouts.
 */
export function paletteShortcutFor( event: KeyboardEvent, apple: boolean = isApplePlatform() ): PaletteShortcut {
    if ( event.defaultPrevented || event.isComposing || event.repeat ) return null;

    const primary = apple ? event.metaKey : event.ctrlKey;
    const other = apple ? event.ctrlKey : event.metaKey;
    if ( primary && !other && !event.altKey && !event.shiftKey && event.key.toLowerCase() === "k" ) {
        return "toggle";
    }

    if ( event.key === "/" && !event.metaKey && !event.ctrlKey && !event.altKey ) {
        const target = event.target;
        if ( isEditableTarget( target ) ) return null;
        if ( target instanceof Element && target.closest( "dialog, [role='dialog']" ) ) return null;
        return "open";
    }

    return null;
}
